import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import {
  appendAttachmentToFormData,
  concatUrl,
  getCustomConfig,
  getNewAbortHandler,
  handleAttachmentBatchResponses,
} from "src/util/http/httpHelper"
import { Attachment, AttachmentUpload } from "src/util/types/dataModels/attachment"
import { BatchErrors, ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  CorrectiveWorkOnItem,
  CorrectiveWorkOnItemFilterParams,
  PerformedWorkComment,
  PerformedWorkCommentPayload,
  PerformedWorkFilterParams,
  RegularWorkOnItem,
  RegularWorkOnItemFilterParams,
} from "src/util/types/dataModels/performedWork"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_PERFORMED_WORK,
} from "src/util/types/dataModels/user"

//region Corrective Work Item

/**
 * Return the list of all Performed Corrective Work on all the Items
 *
 * @param config custom configurations and filtering params
 */
export async function getCorrectiveWorkOnItemList(
  config: HttpConfig<CorrectiveWorkOnItemFilterParams> = {}
): Promise<ObjectList<CorrectiveWorkOnItem>> {
  const entityURL = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create new Performed Corrective Work on Item
 *
 * @param config custom configurations and filtering params
 * @param work the new Performed Corrective Work
 */
export async function postCorrectiveWorkOnItem(
  config: HttpConfig = {},
  work: PayloadEntity<CorrectiveWorkOnItem>
): Promise<CorrectiveWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, work, { signal, customConfig })

  return data
}

/**
 * Retrieve a Corrective Work on Item
 *
 * @param config custom configurations and filtering params
 * @param workId
 */
export async function getCorrectiveWorkOnItem(
  config: HttpConfig = {},
  workId: string
): Promise<CorrectiveWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ID.replace("{id}", workId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Corrective Work on Item
 *
 * @param config custom configurations and filtering params
 * @param workId the id of the Corrective Work on Item that should be updated
 * @param correctiveWork the updated Corrective Work on Item
 */
export async function putCorrectiveWorkOnItem(
  config: HttpConfig = {},
  workId: string,
  correctiveWork: PayloadEntity<CorrectiveWorkOnItem>
): Promise<CorrectiveWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ID.replace("{id}", workId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, correctiveWork, { signal, customConfig })

  return data
}

/**
 * Partially update a Corrective Work On Item
 *
 * @param config custom configurations and filtering params
 * @param workId the id of the Corrective Work On Item that should be partially updated
 * @param correctiveWork the updated Corrective Work
 */
export async function patchCorrectiveWorkOnItem(
  config: HttpConfig = {},
  workId: string,
  correctiveWork: Partial<PayloadEntity<CorrectiveWorkOnItem>>
): Promise<CorrectiveWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ID.replace("{id}", workId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, correctiveWork, { signal, customConfig })

  return data
}

/**
 * Create Corrective Work On Item in bulk
 *
 * @param config custom configurations and filtering params
 * @param workList
 */
export async function postCorrectiveWorkOnItemBatch(
  config: HttpConfig = {},
  workList: PayloadEntity<CorrectiveWorkOnItem>[]
): Promise<CorrectiveWorkOnItem[]> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, workList, { signal, customConfig })

  return data
}

/**
 * Partially update Corrective Work On Item in bulk.
 * The primary keys of the corrective work, which are being updated
 * must be included as part of the payload data.
 *
 * @param config custom configurations and filtering params
 * @param workList
 */
export async function patchCorrectiveWorkOnItemBatch(
  config: HttpConfig = {},
  workList: Partial<CorrectiveWorkOnItem>[]
): Promise<CorrectiveWorkOnItem[]> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, workList, { signal, customConfig })

  return data
}

//endregion

//region Corrective Work Item Attachments

export async function getCorrectiveWorkOnItemAttachmentList(
  config: HttpConfig = {},
  work_id: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ATTACHMENTS_LIST.replace(
    "{work_id}",
    work_id
  )
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["corrective_work_item"]
    return i
  })

  return data
}

export async function postCorrectiveWorkOnItemAttachment(
  config: HttpConfig = {},
  work_id: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ATTACHMENTS_LIST.replace("{work_id}", work_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["corrective_work_item"]

  return data
}

export async function getCorrectiveWorkOnItemAttachment(
  config: HttpConfig = {},
  work_id: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ATTACHMENTS_ID.replace(
    "{work_id}",
    work_id
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["corrective_work_item"]

  return data
}

export async function getCorrectiveWorkOnItemAttachmentContent(
  config: HttpConfig = {},
  work_id: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ATTACHMENTS_ID_CONTENT.replace(
    "{work_id}",
    work_id
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postCorrectiveWorkOnItemAttachmentBatch(
  config: HttpConfig = {},
  work_id: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_ATTACHMENTS_BATCH.replace("{work_id}", work_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "corrective_work_item")
}

//endregion

//region Corrective Work Item Comments

/**
 * Get the list of all Corrective Work on Item Comments
 *
 * @param config custom configurations and filtering params
 */
export async function getCorrectiveWorkOnItemCommentList(
  config: HttpConfig<PerformedWorkFilterParams> = {}
): Promise<ObjectList<PerformedWorkComment>> {
  const entityURL = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Corrective Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param comment the new Corrective Work Comment
 */
export async function postCorrectiveWorkOnItemComment(
  config: HttpConfig = {},
  comment: PerformedWorkCommentPayload
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comment, { signal, customConfig })

  return data
}

/**
 * Get a Corrective Work on Item Comment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId
 */
export async function getCorrectiveWorkOnItemComment(
  config: HttpConfig = {},
  commentId: string
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing Corrective Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId the id of the Corrective Work Comment that should be updated
 * @param comment the updated Corrective Work Comment
 */
export async function putCorrectiveWorkOnItemComment(
  config: HttpConfig<PerformedWorkFilterParams> = {},
  commentId: string,
  comment: PayloadEntity<PerformedWorkComment>
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, comment, { signal, customConfig })

  return data
}

/**
 * Partially update a Corrective Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId the id of the Corrective Work Comment that should be partially updated
 * @param comment the updated Corrective Work Comment
 */
export async function patchCorrectiveWorkOnItemComment(
  config: HttpConfig = {},
  commentId: string,
  comment: Partial<PayloadEntity<PerformedWorkComment>>
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comment, { signal, customConfig })

  return data
}

/**
 * Create Corrective Work on Item Comments in batch
 *
 * @param config custom configurations and filtering params
 * @param comments
 */
export async function postCorrectiveWorkOnItemCommentBatch(
  config: HttpConfig = {},
  comments: PerformedWorkCommentPayload[]
): Promise<ObjectList<PerformedWorkComment> | ObjectList<BatchErrors | PerformedWorkComment>> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comments, { signal, customConfig })

  return data
}

/**
 * Partially update Corrective Work on Item Comments in batch
 *
 * The primary keys of the corrective work comments, which are being updated
 * must be included as part of the payload data
 *
 * @param config custom configurations and filtering params
 * @param comments
 */
export async function patchCorrectiveWorkOnItemCommentBatch(
  config: HttpConfig = {},
  comments: Partial<PerformedWorkComment>[]
): Promise<ObjectList<PerformedWorkComment> | ObjectList<BatchErrors | PerformedWorkComment>> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comments, { signal, customConfig })

  return data
}

//endregion

//region Corrective Work Item Comment Attachments

/**
 * Get a list of all the Attachments of a Corrective Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Corrective Work Comment where the Attachments belong to
 */
export async function getCorrectiveWorkOnItemCommentAttachmentList(
  config: HttpConfig = {},
  commentId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["corrective_work_item_comment"]
    return i
  })

  return data
}

/**
 * Create a new Corrective Work on Item Comment Attachment
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Corrective Work Comment where the Attachment will be added
 * @param attachment Attachment to be uploaded
 */
export async function postCorrectiveWorkOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["corrective_work_item_comment"]

  return data
}

/**
 * Get a Corrective Work on Item Comment Attachment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Corrective Work Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function getCorrectiveWorkOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["corrective_work_item_comment"]

  return data
}

/**
 * Delete a Corrective Work on Item Comment Attachment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Corrective Work Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function deleteCorrectiveWorkOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
) {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

/**
 * Get a Corrective Work on Item Comment Attachment content by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Corrective Work Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function getCorrectiveWorkOnItemCommentAttachmentContent(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS_ID_CONTENT.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

/**
 * Create Corrective Work on Item Comment Attachments in batch
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Corrective Work Comment where the Attachment belongs to
 * @param attachments Attachments to be uploaded
 */
export async function postCorrectiveWorkOnItemCommentAttachmentBatch(
  config: HttpConfig = {},
  commentId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.PERFORMED_WORK_CORRECTIVE_ITEM_COMMENTS_ATTACHMENTS_BATCH.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "corrective_work_item_comment")
}

//endregion

//region Regular Work Item

/**
 * Return the list of all Performed Regular Work on all the Items
 *
 * @param config custom configurations and filtering params
 */
export async function getRegularWorkOnItemList(
  config: HttpConfig<RegularWorkOnItemFilterParams> = {}
): Promise<ObjectList<RegularWorkOnItem>> {
  const entityURL = CSIurl.PERFORMED_WORK_REGULAR_ITEM_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create new Performed Regular Work on Item
 *
 * @param config custom configurations and filtering params
 * @param work the new Performed Regular Work
 */
export async function postRegularWorkOnItem(
  config: HttpConfig = {},
  work: PayloadEntity<RegularWorkOnItem>
): Promise<RegularWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_LIST
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, work, { signal, customConfig })

  return data
}

/**
 * Retrieve a Regular Work on Item
 *
 * @param config custom configurations and filtering params
 * @param workId
 */
export async function getRegularWorkOnItem(
  config: HttpConfig = {},
  workId: string
): Promise<RegularWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ID.replace("{id}", workId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Regular Work on Item
 *
 * @param config custom configurations and filtering params
 * @param workId the id of the Regular Work on Item that should be updated
 * @param regularWork the updated Regular Work on Item
 */
export async function putRegularWorkOnItem(
  config: HttpConfig = {},
  workId: string,
  regularWork: PayloadEntity<RegularWorkOnItem>
): Promise<RegularWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ID.replace("{id}", workId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, regularWork, { signal, customConfig })

  return data
}

/**
 * Partially update a Regular Work On Item
 *
 * @param config custom configurations and filtering params
 * @param workId the id of the Regular Work On Item that should be partially updated
 * @param regularWork the updated Regular Work
 */
export async function patchRegularWorkOnItem(
  config: HttpConfig = {},
  workId: string,
  regularWork: Partial<PayloadEntity<RegularWorkOnItem>>
): Promise<RegularWorkOnItem> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ID.replace("{id}", workId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, regularWork, { signal, customConfig })

  return data
}

/**
 * Create Regular Work On Item in bulk
 *
 * @param config custom configurations and filtering params
 * @param workList
 */
export async function postRegularWorkOnItemBatch(
  config: HttpConfig = {},
  workList: PayloadEntity<RegularWorkOnItem>[]
): Promise<RegularWorkOnItem[]> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, workList, { signal, customConfig })

  return data
}

/**
 * Partially update Regular Work On Item in bulk.
 * The primary keys of the regular work, which are being updated
 * must be included as part of the payload data.
 *
 * @param config custom configurations and filtering params
 * @param workList
 */
export async function patchRegularWorkOnItemBatch(
  config: HttpConfig = {},
  workList: Partial<RegularWorkOnItem>[]
): Promise<RegularWorkOnItem[]> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, workList, { signal, customConfig })

  return data
}

//endregion

//region Regular Work Item Attachments

export async function getRegularWorkOnItemAttachmentList(
  config: HttpConfig = {},
  work_id: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ATTACHMENTS_LIST.replace(
    "{work_id}",
    work_id
  )
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["regular_work_item"]
    return i
  })

  return data
}

export async function postRegularWorkOnItemAttachment(
  config: HttpConfig = {},
  work_id: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ATTACHMENTS_LIST.replace("{work_id}", work_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["regular_work_item"]

  return data
}

export async function getRegularWorkOnItemAttachment(
  config: HttpConfig = {},
  work_id: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ATTACHMENTS_ID.replace(
    "{work_id}",
    work_id
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["regular_work_item"]

  return data
}

export async function getRegularWorkOnItemAttachmentContent(
  config: HttpConfig = {},
  work_id: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ATTACHMENTS_ID_CONTENT.replace(
    "{work_id}",
    work_id
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postRegularWorkOnItemAttachmentBatch(
  config: HttpConfig = {},
  work_id: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_ATTACHMENTS_BATCH.replace("{work_id}", work_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "regular_work_item")
}

//endregion

//region Regular Work Item Comments

/**
 * Get the list of all Regular Work on Item Comments
 *
 * @param config custom configurations and filtering params
 */
export async function getRegularWorkOnItemCommentList(
  config: HttpConfig<PerformedWorkFilterParams> = {}
): Promise<ObjectList<PerformedWorkComment>> {
  const entityURL = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Regular Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param comment the new Regular Work Comment
 */
export async function postRegularWorkOnItemComment(
  config: HttpConfig = {},
  comment: PerformedWorkCommentPayload
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comment, { signal, customConfig })

  return data
}

/**
 * Get a Regular Work on Item Comment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId
 */
export async function getRegularWorkOnItemComment(
  config: HttpConfig = {},
  commentId: string
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing Regular Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId the id of the Regular Work Comment that should be updated
 * @param comment the updated Regular Work Comment
 */
export async function putRegularWorkOnItemComment(
  config: HttpConfig<PerformedWorkFilterParams> = {},
  commentId: string,
  comment: PayloadEntity<PerformedWorkComment>
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, comment, { signal, customConfig })

  return data
}

/**
 * Partially update a Regular Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId the id of the Regular Work Comment that should be partially updated
 * @param comment the updated Regular Work Comment
 */
export async function patchRegularWorkOnItemComment(
  config: HttpConfig = {},
  commentId: string,
  comment: Partial<PayloadEntity<PerformedWorkComment>>
): Promise<PerformedWorkComment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ID.replace("{id}", commentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comment, { signal, customConfig })

  return data
}

/**
 * Create Regular Work on Item Comments in batch
 *
 * @param config custom configurations and filtering params
 * @param comments
 */
export async function postRegularWorkOnItemCommentBatch(
  config: HttpConfig = {},
  comments: PerformedWorkCommentPayload[]
): Promise<ObjectList<PerformedWorkComment> | ObjectList<BatchErrors | PerformedWorkComment>> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, comments, { signal, customConfig })

  return data
}

/**
 * Partially update Regular Work on Item Comments in batch
 *
 * The primary keys of the corrective work comments, which are being updated
 * must be included as part of the payload data
 *
 * @param config custom configurations and filtering params
 * @param comments
 */
export async function patchRegularWorkOnItemCommentBatch(
  config: HttpConfig = {},
  comments: Partial<PerformedWorkComment>[]
): Promise<ObjectList<PerformedWorkComment> | ObjectList<BatchErrors | PerformedWorkComment>> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_BATCH
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, comments, { signal, customConfig })

  return data
}

//endregion

//region Regular Work Item Comment Attachment

/**
 * Get a list of all the Attachments of a Regular Work on Item Comment
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Regular Work Comment where the Attachments belong to
 */
export async function getRegularWorkOnItemCommentAttachmentList(
  config: HttpConfig = {},
  commentId: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["regular_work_item_comment"]
    return i
  })

  return data
}

/**
 * Create a new Regular Work on Item Comment Attachment
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Regular Work Comment where the Attachment will be added
 * @param attachment Attachment to be uploaded
 */
export async function postRegularWorkOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["regular_work_item_comment"]

  return data
}

/**
 * Get a Regular Work on Item Comment Attachment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Regular Work Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function getRegularWorkOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["regular_work_item_comment"]

  return data
}

/**
 * Delete a Regular Work on Item Comment Attachment by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Regular Work Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function deleteRegularWorkOnItemCommentAttachment(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
) {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS_ID.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

/**
 * Get a Regular Work on Item Comment Attachment content by ID
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Regular Work Comment where the Attachment belongs to
 * @param attachmentId ID of the Attachment
 */
export async function getRegularWorkOnItemCommentAttachmentContent(
  config: HttpConfig = {},
  commentId: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS_ID_CONTENT.replace(
    "{comment_id}",
    commentId
  ).replace("{id}", attachmentId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

/**
 * Create Regular Work on Item Comment Attachments in batch
 *
 * @param config custom configurations and filtering params
 * @param commentId ID of the Regular Work Comment where the Attachment belongs to
 * @param attachments Attachments to be uploaded
 */
export async function postRegularWorkOnItemCommentAttachmentBatch(
  config: HttpConfig = {},
  commentId: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.PERFORMED_WORK_REGULAR_ITEM_COMMENTS_ATTACHMENTS_BATCH.replace(
    "{comment_id}",
    commentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_PERFORMED_WORK,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "regular_work_item_comment")
}

//endregion
